/**
 * Which chart the toolbar drives, and which one a screener row charts to.
 *
 * A layout can hold several chart sections, and one of them has to be the
 * target. Clicking a section makes it active; this resolves that id — which may
 * be stale, may name something that is not a chart, or may not exist yet — into
 * the section the rest of the app should act on.
 *
 * **The fallback carries the weight.** `activeSectionId` is session state and
 * outlives the thing it names in three ordinary ways:
 *
 * 1. Nothing has been clicked yet, so it is `null`.
 * 2. It names a *screener*, left over from before clicking one stopped claiming
 *    activation, or from a layout where that section was a chart. A screener
 *    cannot be what a symbol is charted into.
 * 3. It names a section on a different layout, because switching tabs does not
 *    clear it and should not: coming back should land where you left off.
 *
 * In all three the toolbar still has to drive some chart, so the first is
 * returned. A toolbar bound to nothing is a toolbar that silently stops
 * working, which is worse than one bound to a chart you did not pick.
 *
 * Kept as a pure function rather than inline in `App` because the rule above is
 * where the edge cases live, and `SectionGrid` cannot be rendered in a test —
 * it reaches SciChart, which needs WebGL and a WASM engine jsdom does not have.
 */

import type { Section } from "@qufolio/model";

/**
 * Whether clicking this section should make it the active one.
 *
 * **Only a chart.** A screener click used to activate too, on the reasoning
 * that you were working in it — and the fallback below absorbed it, so nothing
 * looked broken. What it actually did was hand the toolbar, the ring and the
 * next charted symbol to `charts[0]`: with two charts and the second one
 * active, touching the screener moved all three to the first, for a reason
 * nothing on screen explained.
 *
 * A screener has nothing to be active *for* — every control that reads this
 * targets a chart. So the click is left alone and the active chart stays where
 * the user put it. The fallback keeps its job for the ids that still arrive
 * stale, which is what it was for.
 */
export function claimsActivation(section: Section): boolean {
  return section.kind === "chartGroup";
}

/** Every chart section in a layout, in contract order. */
export function chartSections(sections: readonly Section[] | undefined): Section[] {
  return (sections ?? []).filter((section) => section.kind === "chartGroup");
}

/**
 * The chart section to act on, or `undefined` when a layout holds no chart.
 *
 * `undefined` is a real answer, not a failure: a screener-only layout is legal,
 * and the caller renders no toolbar for it.
 */
export function activeChartSection(
  sections: readonly Section[] | undefined,
  activeSectionId: string | null,
): Section | undefined {
  const charts = chartSections(sections);
  return charts.find((section) => section.id === activeSectionId) ?? charts[0];
}

/**
 * Whether the active-section ring should be drawn at all.
 *
 * One chart has nothing to be distinguished from, so ringing it says only that
 * the feature exists. The ring is an answer to "which of these", and with one
 * chart there is no question.
 */
export function showsActiveRing(sections: readonly Section[] | undefined): boolean {
  return chartSections(sections).length > 1;
}

/**
 * The border that marks the selected chart.
 *
 * **Paint only — no geometry.** It is spread onto an overlay the cell positions
 * absolutely over itself, never onto the cell, so it cannot change a track's
 * size: a real `border` on the cell adds two pixels to the content box, which
 * resizes the WebGL canvas every time the selection moves between two charts.
 *
 * Two candidates were tried on the running app and both are invisible over a
 * chart, which is the one place this has to work:
 *
 * - `box-shadow: inset` — an inset shadow paints above the element's own
 *   background but **below its children**, and SciChart's canvas is opaque and
 *   fills the cell. This is what the ring used to be, and it survived only
 *   because a 1px line at the cell's edge looked plausible either way.
 * - `outline` with a negative offset — the spec draws outlines last, but the
 *   canvas is composited and paints over it anyway. Measured: the outline
 *   showed around the chart's *toolbar* row and stopped at the canvas.
 *
 * Two pixels, not one. This answers "which of these charts does a screener row
 * chart into", and at one pixel in the accent colour it read as the seam between
 * two panels rather than as a selection.
 */
export const ACTIVE_BORDER = "2px solid var(--qf-accent)";

/**
 * The ring for one cell, or nothing.
 *
 * A style fragment rather than a boolean, so the colour stays in one place and
 * the component spreads it — no component holds a colour, and `var(--qf-accent)`
 * is the only spelling of this one anywhere.
 */
export function activeRingStyle(
  sections: readonly Section[] | undefined,
  sectionId: string,
  activeSectionId: string | null | undefined,
): { border: string } | undefined {
  if (!showsActiveRing(sections)) return undefined;
  return sectionId === activeSectionId ? { border: ACTIVE_BORDER } : undefined;
}
